import { useEffect, useState, useCallback, useRef } from 'react';
import { StatusCodes } from 'http-status-codes';
import {
  Box,
  Container,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  type SelectChangeEvent,
} from '@mui/material';
import PageTitle from '../../components/PageTitle';
import { ReactComponent as Calendar } from '../../assets/icons/calendar.svg';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import whitelabelThunk from '../../store/whitelabel/whitelabelThunk';
import SkiptraceStatsCard from './components/SkiptraceStatsCard';
import UserStatsCard from './components/UserStatsCard';
import MRRSubscriptionTierCard from './components/MRRSubscriptionTierCard';
import KeyPerformanceCard from './components/KeyPerformanceCard';
import useSubdomain from '../../utils/useSubdomain';
import { adminAPI } from '../../api/services/userApi';
import type { AccountType } from '../../types/userTypes';
import { setLoading, unsetLoading } from '../../store/whitelabel/whitelabelSlice';
import StyledWhitelabelPageAdmin from './WhiteLabelPageAdmin.styles';
import DropDownList from '../DashboardPage/components/DropDownList/DropDownList';
import { formattingDate } from '../../utils/formattingDateHelper';

const WhitelabelPageAdmin = () => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector((state) => state.whiltelabelSlice.addition.isLoading);
  const user = useAppSelector((state) => state.userSlice.user);
  const [accountId, setAccountId] = useState<string>('');
  const [whiteLabels, setWhiteLabels] = useState<AccountType[]>([]);
  const { isMainAppSubDomain } = useSubdomain();
  const wrapperRef = useRef(null);
  const [isOpenDropDownList, setIsOpenDropDownList] = useState(false);
  const [filter, setFilter] = useState({ startDate: null, endDate: null });

  const onSetFilter = (title: string, value: string | Date | null) => {
    setFilter((prev) => ({ ...prev, [title]: value }));
  };

  const onToggle = () => {
    setIsOpenDropDownList((prev) => !prev);
  };

  const onClose = () => {
    setIsOpenDropDownList(false);
  };

  const handleChange = useCallback((event: SelectChangeEvent<string>) => {
    setAccountId(event.target.value);
  }, []);

  useEffect(() => {
    if (!isMainAppSubDomain) return;

    const fetchAccounts = async () => {
      try {
        const res = await adminAPI.getAccountList();
        if (res.status === StatusCodes.OK) {
          setWhiteLabels(res.data.data?.accounts || []);
        }
      } catch (error) {
        console.error('Failed to fetch accounts:', error);
      }
    };

    fetchAccounts();
  }, [isMainAppSubDomain]);

  useEffect(() => {
    if (user.account?.id) {
      setAccountId(user.account.id.toString());
    }
  }, [user.account?.id]);

  const fetchStatsWithFilter = useCallback(async () => {
    try {
      if (!accountId || !filter.startDate || !filter.endDate) return;
      dispatch(setLoading());
      await dispatch(whitelabelThunk.getSkiptraceStats({ accountId, ...filter }));
      dispatch(unsetLoading());
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [accountId, dispatch, filter]);

  useEffect(() => {
    if (!accountId) return;
    const fetchData = async () => {
      try {
        dispatch(setLoading());
        await Promise.all([
          dispatch(whitelabelThunk.getSkiptraceStats({ accountId })),
          dispatch(whitelabelThunk.getUserStats(accountId)),
        ]);
        dispatch(unsetLoading());
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };

    fetchData();
  }, [accountId, dispatch]);

  useEffect(() => {
    fetchStatsWithFilter();
  }, [fetchStatsWithFilter]);

  return (
    <Box
      sx={{
        py: 3,
        px: 4,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <PageTitle
        title="KPI Dashboard"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {isMainAppSubDomain ? (
          <Box width={300}>
            <FormControl fullWidth disabled={whiteLabels.length === 0}>
              <InputLabel id="account-select-label">Select Account</InputLabel>
              <Select
                labelId="account-select-label"
                value={accountId}
                label="Select Account"
                onChange={handleChange}
              >
                {whiteLabels.map((acc) => (
                  <MenuItem key={acc.id} value={acc.id}>
                    {acc.accountName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        ) : null}
      </PageTitle>

      {isLoading ? (
        <div className="styled-app__loader" />
      ) : (
        <Container maxWidth={false} disableGutters sx={{ maxWidth: '100%' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <StyledWhitelabelPageAdmin>
                <Box sx={{ width: { xs: '100%', lg: '50%', xl: '35%' } }}>
                  <div ref={wrapperRef}>
                    <div className="date-picker-area text-4" onClick={onToggle}>
                      <div className="date-picker">
                        {!filter.startDate
                          ? (
                            <div className="default-text">Start date</div>
                          ) : (
                            <div>{formattingDate(filter.startDate)}</div>
                          )
                        }
                        <Calendar />
                      </div>
                      <div className="default-text">to</div>
                      <div className="date-picker">
                        {!filter.endDate
                          ? (
                            <div className="default-text">End date</div>
                          ) : (
                            <div>{formattingDate(filter.endDate)}</div>
                          )}
                        <Calendar />
                      </div>
                    </div>

                    {
                      isOpenDropDownList ? (
                        <div className="date-area">
                          <DropDownList
                            filter={filter}
                            setFilter={onSetFilter}
                            onCloseDropDownList={onClose}
                          />
                        </div>
                      ) : null
                    }
                  </div>
                </Box>
              </StyledWhitelabelPageAdmin>
            </Grid>
            <Grid item xs={12} md={6}>
              <KeyPerformanceCard />
            </Grid>
            <Grid item xs={12} md={6}>
              <MRRSubscriptionTierCard />
            </Grid>
            <Grid item xs={12} md={6}>
              <SkiptraceStatsCard />
            </Grid>
            <Grid item xs={12} md={6}>
              <UserStatsCard />
            </Grid>
          </Grid>
        </Container>
      )}
    </Box>
  );
};

export default WhitelabelPageAdmin;
